import { useContractWrite, usePrepareContractWrite, useNetwork, useWaitForTransaction } from 'wagmi';
import { getV2Contracts } from '@/config/dex-v3-config';
import { uniswapV2RouterABI } from '@/config/abis/uniswapV2Router';
import { useToast } from '@/components/ui/use-toast';
import { parseEther } from 'viem';

interface AddV2LiquidityParams {
  token: string;
  tokenAmount: string;
  nativeAmount: string;
  slippageTolerance?: number;
  recipient?: string;
}

/**
 * Hook to add liquidity to a V2 pair (with native token)
 */
export function useAddV2Liquidity(params: AddV2LiquidityParams | null) {
  const { chain } = useNetwork();
  const { toast } = useToast();
  const v2Contracts = chain?.id ? getV2Contracts(chain.id) : null;

  const slippage = params?.slippageTolerance || 0.5;
  const slippageMultiplier = 1 - slippage / 100;

  const tokenAmount = params?.tokenAmount ? parseEther(params.tokenAmount) : BigInt(0);
  const nativeAmount = params?.nativeAmount ? parseEther(params.nativeAmount) : BigInt(0);
  
  const tokenAmountMin = BigInt(Math.floor(Number(tokenAmount) * slippageMultiplier));
  const nativeAmountMin = BigInt(Math.floor(Number(nativeAmount) * slippageMultiplier));

  const deadline = BigInt(Math.floor(Date.now() / 1000) + 1200); // 20 minutes

  const { config } = usePrepareContractWrite({
    address: v2Contracts?.router as `0x${string}`,
    abi: uniswapV2RouterABI,
    functionName: 'addLiquidityETH',
    args: params && params.recipient ? [
      params.token as `0x${string}`,
      tokenAmount,
      tokenAmountMin,
      nativeAmountMin,
      params.recipient as `0x${string}`,
      deadline,
    ] : undefined,
    value: nativeAmount,
    enabled: Boolean(v2Contracts?.router && params && tokenAmount > 0 && nativeAmount > 0),
  });

  const { write, data, isLoading: isWriting } = useContractWrite(config);

  const { isLoading: isConfirming, isSuccess } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess: () => {
      toast({
        title: 'Liquidity Added!',
        description: 'Your V2 liquidity has been added successfully.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Transaction Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const addLiquidity = () => {
    if (write) {
      write();
    }
  };

  return {
    addLiquidity,
    isAdding: isWriting || isConfirming,
    isSuccess,
    canAdd: Boolean(write),
  };
}
